import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { contentBoxStyle, imageContainerStyle } from "../styles/commonStyles";
import colors from "../styles/colors";
import StyledButton, {
  StyledButtonBlack,
} from "../components/StyledButton/StyledButton";

// Web Development Icons
import { FaCss3Alt, FaHtml5, FaReact, FaDocker, FaFigma } from "react-icons/fa";
import { BiLogoTypescript } from "react-icons/bi";

// Design Icons
import {
  SiAdobephotoshop,
  SiBlender,
  SiAdobeindesign,
  SiAdobeillustrator,
} from "react-icons/si";

// Development Tools Icons
import { VscVscode } from "react-icons/vsc";
import { FiGitlab } from "react-icons/fi";
import { LuAudioLines } from "react-icons/lu";
import { AiOutlineTool } from "react-icons/ai";
import { MdPhotoCamera } from "react-icons/md";
import MoveableImage from "../components/MoveableImage/MoveableImage";

const Me = `${process.env.PUBLIC_URL}/me.png`;

// Shared styles
const sharedContainerStyle = {
  width: "100%",
  maxWidth: "500px",
  height: "30rem",
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50px 0px 50px 0px",
  backgroundColor: colors.filterColors[5],
};

const iconContainerStyle = {
  display: "flex",
  flexWrap: "wrap" as const,
  justifyContent: "center",
  alignItems: "center",
  maxWidth: "300px",
};

const iconStyle = {
  size: "3rem",
  style: {
    margin: "0.5rem",
    padding: "0.5rem",
    borderRadius: "0.5rem",
    backgroundColor: "white",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
  },
};

interface SkillSectionProps {
  title: string;
  icons: JSX.Element[];
  description: string;
}

const SkillSection: React.FC<SkillSectionProps> = ({
  title,
  icons,
  description,
}) => (
  <Box sx={sharedContainerStyle}>
    <Typography variant="h2" textAlign="center" paddingX="1rem" color="white">
      {title}
    </Typography>
    <Divider sx={{ width: "30%", borderColor: "white", paddingTop: "1rem" }} />
    <Box
      sx={{
        ...iconContainerStyle,
        paddingTop: "1.5rem",
        paddingBottom: "1rem",
      }}
    >
      {icons}
    </Box>
    <Typography
      variant="body1"
      textAlign="center"
      paddingX="2rem"
      color="white"
    >
      {description}
    </Typography>
  </Box>
);

const AboutMe: React.FC = () => {
  const webDevIcons = [
    <FaReact
      key="react"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(97, 218, 251)" }}
    />,
    <BiLogoTypescript
      key="typescript"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(49, 120, 198)" }}
    />,
    <FaHtml5
      key="html"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(227, 79, 38)" }}
    />,
    <FaCss3Alt
      key="css"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(21, 114, 182)" }}
    />,
  ];

  const designIcons = [
    <FaFigma
      key="figma"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(54, 54, 54)" }}
    />,
    <SiAdobeillustrator
      key="illustrator"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(255, 154, 0)" }}
    />,
    <SiAdobephotoshop
      key="photoshop"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(0, 122, 204)" }}
    />,
    <SiAdobeindesign
      key="indesign"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "#fd3265" }}
    />,
    <SiBlender
      key="blender"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(245, 121, 42)" }}
    />,
  ];

  const devToolsIcons = [
    <VscVscode
      key="vscode"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(0, 122, 204)" }}
    />,
    <FiGitlab
      key="gitlab"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(252, 109, 38)" }}
    />,
    <FaDocker
      key="docker"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "rgb(36, 150, 237)" }}
    />,
  ];

  const creativeSkillsIcons = [
    <MdPhotoCamera
      key="camera"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "#93add5" }}
    />,
    <LuAudioLines
      key="audio"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "#d6af88" }}
    />,
    <AiOutlineTool
      key="tool"
      {...iconStyle}
      style={{ ...iconStyle.style, color: "#b089b0" }}
    />,
  ];

  // Email protection function
  const handleEmailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const username = "pirgeongirleater";
    const domain = "gmail.com";
    window.location.href = `mailto:${username}@${domain}`;
  };

  return (
    <Box id="content" sx={contentBoxStyle}>
      <Box
        id="text-container"
        sx={{
          maxWidth: "1564px",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 2fr",
          },
          gap: "2rem",
          paddingTop: "10rem",
          paddingBottom: "5rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "white",
            wordBreak: "break-word",
            borderRadius: "0px 0px 0px 0px",
            boxShadow: "2px 2px 4px rgba(90, 153, 109, 0.5)",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: colors.filterColors[5],
              margin: "1rem",
            }}
          >
            About Me
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            paddingX: "1.8rem",
            borderRadius: "50px 0px 50px 0px",
            backgroundColor: colors.filterColors[5],
          }}
        >
          <Box
            sx={{
              height: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              paddingTop: "2rem",
              paddingBottom: "1rem",
            }}
          >
              <Box
                component="img"
                src={Me}
                alt="Hendrik Grygas"
                sx={{
                  borderRadius: "50%",
                  maxWidth: "300px",
                  height: "auto",
                  objectFit: "contain",
                }}
              />

          
          </Box>
          <Typography textAlign="center" paddingBottom="1rem">
            <p>
              Ahoi Hoi, I'm Hendrik, a multidisciplinary creative professional
              based in Germany. My expertise lies in design, web development and
              various fields that require creative solutions. Focusing on
              combining all my skills to create unique and functional products.
            </p>
            <p>
              My goal is constant learning, getting better at new things and
              improving my skills. This is not only limited to the technical
              side, but also the soft skills, which I am improving with every
              day of coordinating projects and working with clients.
            </p>
          </Typography>
          <Box sx={{ paddingBottom: "4rem" }}>
              <a
                href="#contact"
                onClick={handleEmailClick}
                style={{ textDecoration: "none" }}
              >
                <StyledButton>Get in Touch</StyledButton>
              </a>
            </Box>
        </Box>
      </Box>

      <Box id="image-container" sx={imageContainerStyle}>
        <SkillSection
          title="Web Development"
          icons={webDevIcons}
          description="Proficient in React, TypeScript, HTML, CSS, and modern web technologies"
        />

        <SkillSection
          title="Design"
          icons={designIcons}
          description="Experienced with Figma, Adobe Illustrator, Photoshop, InDesign, 3D modeling, and animation with Blender"
        />

        <SkillSection
          title="Development Tools"
          icons={devToolsIcons}
          description="Experienced with VS Code/Cursor. Basic knowledge of GitLab, Docker, and various modern development workflows"
        />

        <SkillSection
          title="Creative Skills"
          icons={creativeSkillsIcons}
          description="Experienced in photography, audio composition, and traditional craftsmanship including jewelry making and metalwork"
        />
      </Box>
    </Box>
  );
};

export default AboutMe;
